import { Label } from './Label';
import { Circle } from '../types/Circle.ts';
import { CircleFactory } from './CircleFactory';
import { Position } from './Position';
import { RelativeRadius } from './RelativeRadius';
import { ChartContext } from './ChartContext';
import { Categories } from './Categories';
import {CategoryData} from "../types/CategoryData.ts";

export class RadarChart {
    private readonly canvas: HTMLCanvasElement;
    private readonly ctx: CanvasRenderingContext2D;
    private circles: Circle[] = [];
    private numberLabels: Label[] = [];
    private readonly categoriesData: CategoryData[];
    private categories!: Categories;

    constructor(categoriesData: CategoryData[]) {
        this.categoriesData = categoriesData;

        const canvas = document.getElementById('radarCanvas') as HTMLCanvasElement;
        const context = canvas.getContext('2d');

        if (!context) {
            throw new Error("Could not get the 2D context of the canvas.");
        }

        this.canvas = canvas;
        this.ctx = context;

        this.setCanvasSize();
        this.prepareObjects();
    }

    private setCanvasSize(): void {
        this.canvas.width = 1200;
        this.canvas.height = 600;
    }

    private prepareObjects(): void {
        const centerX = this.canvas.width / 2;
        const centerY = this.canvas.height / 2;
        const maxRadius = Math.min(centerX, centerY) * 0.85;
        const fontSize = this.canvas.width / 80;
        const centerPosition = new Position(centerX, centerY);
        const margin = 20;
        const chartContext = new ChartContext(this.ctx, this.canvas, centerPosition, maxRadius, fontSize, margin);

        const scales : Circle[] = [
            CircleFactory.createDashed(this.ctx, centerX, centerY, new RelativeRadius(maxRadius, 0.16667), '#555', 1, [5, 5]),
            CircleFactory.createDashed(this.ctx, centerX, centerY, new RelativeRadius(maxRadius, 0.33333), '#555', 1, [5, 5]),
            CircleFactory.createDashed(this.ctx, centerX, centerY, new RelativeRadius(maxRadius, 0.5), '#555', 1, [5, 5]),
            CircleFactory.createDashed(this.ctx, centerX, centerY, new RelativeRadius(maxRadius, 0.66667), '#555', 1, [5, 5]),
            CircleFactory.createDashed(this.ctx, centerX, centerY, new RelativeRadius(maxRadius, 0.83333), '#555', 1, [5, 5]),
            CircleFactory.createDashed(this.ctx, centerX, centerY, new RelativeRadius(maxRadius, 1.0), '#555', 1, [5, 5]),
        ];

        const thresholds : Circle[] = [
            CircleFactory.createSolid(this.ctx, centerX, centerY, new RelativeRadius(maxRadius, 0.85), '#4ECDC4', 4),
            CircleFactory.createSolid(this.ctx, centerX, centerY, new RelativeRadius(maxRadius, 0.59), '#E9D758', 4),
            CircleFactory.createSolid(this.ctx, centerX, centerY, new RelativeRadius(maxRadius, 0.32), '#CC5B64', 4),
        ];

        this.circles.push(...scales);
        this.circles.push(...thresholds);
        this.categories = new Categories(
            chartContext, 
            this.categoriesData);

        // Numbers along the horizontal axis
        const numCount = 6;
        const numSpacing = maxRadius / numCount;
        for (let i = 1; i <= numCount; i++) {
            const radius = i * numSpacing;
            const x = centerX + radius;
            const label = Label.createCentered(
                this.ctx,
                (6 - i).toString(),
                x,
                centerY,
                '#FFF',
                fontSize * 0.8
            );
            this.numberLabels.push(label);
        }
    }

    public draw(): void {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.circles.forEach(obj => obj.draw());
        this.numberLabels.forEach(label => label.draw());
        this.categories.draw();
    }
} 